// ============================================================================
// GST PROFILE MAPPER — G-1 slice 3b (ADR-0025 §5)
// ============================================================================
// Teaches PRIMA a client's own file layout once: pick, for each field, the
// column of the file that holds it, and watch the rate-wise totals move as you
// go. Saving makes a LEARNED profile for this client, PROPOSED — usable on the
// client's submissions straight away, and accepted or rejected by a reviewer
// with the `approve` grant. A saved profile is never edited in place: editing
// one here saves its next version, so a submission that recorded "v2" always
// means the same mapping.
//
// The form covers ordinary registers. A definition the form cannot express
// (summed columns, continuation rows …) is edited as JSON instead, so nothing
// is lost on the way through.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { gstSubmissionService } from 'api/services/gstSubmissionService';
import {
  GST_DOCUMENT_TYPES,
  type GstDataSubmission,
  type GstEngagement,
  type GstMappingField,
  type GstMappingPreview,
  type GstMappingProfile,
  type GstStagingRow,
} from 'api/types/gstEngagement.types';
import {
  CORE_FIELDS,
  DATE_FORMATS,
  FIELD_NAMES,
  GROUP_FIELDS,
  columnLabel,
  emptySheet,
  fromDefinition,
  suggestFields,
  toDefinition,
  type ProfileForm,
  type SheetForm,
} from './gstProfileForm';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement;
  submission: GstDataSubmission;
  /** Edit this LEARNED profile — saves its next version. Null = a new profile. */
  base?: GstMappingProfile | null;
  /** Start a NEW profile from this definition (copying a system profile). Ignored when `base` is set. */
  seedDefinition?: string | null;
  onClose: () => void;
  onSaved: (profile: GstMappingProfile) => void;
}

type Mode = 'form' | 'json';

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const cellText = (c?: { v: string | number | boolean | null; t: string }): string => {
  if (!c || c.v === null || c.v === undefined) return '';
  if (c.t === 'd' && typeof c.v === 'string') return c.v.slice(0, 10);
  return String(c.v);
};

const GstProfileMapperModal: React.FC<Props> = ({ isOpen, engagement, submission, base, seedDefinition, onClose, onSaved }) => {
  const sheets = useMemo(
    () => (submission.parseStats?.sheets ?? []).map((s) => ({ name: s.name, headerCells: s.headerCells ?? [] })),
    [submission],
  );
  const headersOf = useCallback((sheet: string) => sheets.find((s) => s.name === sheet)?.headerCells ?? [], [sheets]);
  const isHsn = submission.documentType === 'HSN_SUMMARY';
  const streamLabel = GST_DOCUMENT_TYPES.find((t) => t.value === submission.documentType)?.label ?? submission.documentType;

  const [vocabulary, setVocabulary] = useState<GstMappingField[]>([]);
  const [mode, setMode] = useState<Mode>('form');
  const [form, setForm] = useState<ProfileForm>({ sheets: [] });
  const [json, setJson] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [summaryBasis, setSummaryBasis] = useState('');
  const [useOnSubmission, setUseOnSubmission] = useState(true);
  const [activeSheet, setActiveSheet] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [samples, setSamples] = useState<Record<string, GstStagingRow[]>>({});
  const [preview, setPreview] = useState<GstMappingPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const snapshotRef = useRef('');

  // ---- open: vocabulary, starting form ---------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    gstSubmissionService.getMappingFields().then(setVocabulary).catch(() => setVocabulary([]));

    let startForm: ProfileForm;
    let startMode: Mode = 'form';
    let startJson = '';
    const startFrom = base?.definition ?? seedDefinition ?? null;
    if (startFrom) {
      const r = fromDefinition(startFrom, sheets);
      if ('form' in r) {
        startForm = r.form;
      } else {
        startForm = { sheets: sheets.map((s) => emptySheet(s.name)) };
        startMode = 'json';
        startJson = startFrom;
        notificationService.toast.info(`This profile uses ${r.unsupported}; it opens as JSON.`);
      }
    } else {
      startForm = {
        sheets: sheets.map((s) => ({ ...emptySheet(s.name), fields: suggestFields(s.headerCells) })),
      };
    }

    setForm(startForm);
    setMode(startMode);
    setJson(startMode === 'json' ? startJson : toDefinition(startForm, headersOf));
    setName(base?.name ?? '');
    setNotes('');
    setSummaryBasis(base?.summaryBasis ?? submission.summaryBasis ?? '');
    setUseOnSubmission(true);
    setActiveSheet(sheets[0]?.name ?? '');
    setShowAll(false);
    setPreview(null);
    setPreviewError(null);
    setFieldErrors({});
    snapshotRef.current = JSON.stringify({
      def: startMode === 'json' ? startJson : toDefinition(startForm, headersOf),
      name: base?.name ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // A few data rows per sheet, so each picked column shows what it holds.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    Promise.all(sheets.map((s) =>
      gstSubmissionService.getStaging(engagement.id, submission.id, { page: 1, pageSize: 3, sheetName: s.name, rowKind: 'DATA' })
        .then((r) => [s.name, r.data] as const)
        .catch(() => [s.name, [] as GstStagingRow[]] as const),
    )).then((pairs) => { if (!cancelled) setSamples(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [isOpen, engagement.id, submission.id, sheets]);

  // The definition being edited: generated from the form, or the JSON as typed.
  const definition = useMemo(
    () => (mode === 'form' ? toDefinition(form, headersOf) : json),
    [mode, form, json, headersOf],
  );

  // ---- live preview (debounced) ----------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const p = await gstSubmissionService.previewMapping(engagement.id, submission.id, {
          definition, page: 1, pageSize: 5, errorsOnly: true,
        });
        setPreview(p);
        setPreviewError(null);
      } catch (err: any) {
        setPreview(null);
        setPreviewError(err?.response?.data?.message ?? 'The definition could not be applied.');
      } finally {
        setPreviewing(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [isOpen, definition, engagement.id, submission.id]);

  const isDirty = useMemo(() => {
    if (!snapshotRef.current) return false;
    return JSON.stringify({ def: definition, name }) !== snapshotRef.current || notes.trim() !== '';
  }, [definition, name, notes]);

  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  // ---- form editing ----------------------------------------------------------
  const current = form.sheets.find((s) => s.sheet === activeSheet);
  const headers = current ? headersOf(current.sheet) : [];

  const updateSheet = useCallback((patch: (s: SheetForm) => SheetForm) => {
    setForm((f) => ({ sheets: f.sheets.map((s) => (s.sheet === activeSheet ? patch(s) : s)) }));
  }, [activeSheet]);

  const setField = (field: string, idx: number | null) =>
    updateSheet((s) => ({ ...s, fields: { ...s.fields, [field]: idx } }));

  const toggleRequired = (field: string) =>
    updateSheet((s) => ({ ...s, required: s.required.includes(field) ? s.required.filter((r) => r !== field) : [...s.required, field] }));

  const visibleFields = useMemo(() => {
    const all = vocabulary.length ? vocabulary.map((f) => f.name) : CORE_FIELDS;
    const mapped = current ? Object.keys(current.fields).filter((k) => current.fields[k] !== null && current.fields[k] !== undefined) : [];
    return showAll ? all : all.filter((f) => CORE_FIELDS.includes(f) || mapped.includes(f));
  }, [vocabulary, showAll, current]);

  const sampleOf = (idx: number | null | undefined): string => {
    if (idx === null || idx === undefined || !current) return '';
    return (samples[current.sheet] ?? []).map((r) => cellText(r.cells[idx])).filter(Boolean).slice(0, 2).join(' · ');
  };

  const columnOptions = (
    <>
      <option value="">— not mapped —</option>
      {headers.map((_, i) => <option key={i} value={i}>{columnLabel(headers, i)}</option>)}
    </>
  );

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    if (next === 'json') { setJson(toDefinition(form, headersOf)); setMode('json'); return; }
    const r = fromDefinition(json, sheets);
    if ('form' in r) { setForm(r.form); setMode('form'); }
    else notificationService.toast.warning(`The JSON uses ${r.unsupported}, which the form cannot show. Keep editing it as JSON.`);
  };

  // ---- save ------------------------------------------------------------------
  const handleSave = () => {
    const errors: Record<string, string> = {};
    if (!base && !name.trim()) errors.name = 'Name the profile — e.g. the client\'s package and report.';
    if (isHsn && !summaryBasis) errors.summaryBasis = 'Say whether the summary is gross or net of credit notes.';
    if (previewError) errors.definition = previewError;
    else if (!preview) errors.definition = 'Wait for the preview — the profile is checked against this file before saving.';
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) setSaveConfirmOpen(true);
  };

  const confirmSave = async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const { profile, message } = await gstSubmissionService.saveMappingProfile(engagement.id, submission.id, {
        name: base ? null : name.trim(),
        definition,
        notes: notes.trim() || null,
        summaryBasis: isHsn ? summaryBasis : null,
        supersedesProfileId: base?.id ?? null,
        useOnSubmission,
      });
      notificationService.toast.success(message);
      onSaved(profile);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not save the profile.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const cell: React.CSSProperties = { padding: '3px 6px', borderTop: '1px solid var(--border-color)', verticalAlign: 'middle' };
  const select: React.CSSProperties = { fontSize: '0.8125rem', padding: '3px 6px', width: '100%' };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {base ? `New version of '${base.name}' (v${base.version} → next)` : 'Map this client\'s columns'} · {streamLabel}
          </h2>
          <button className="modal-close" onClick={handleClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* ---- left: the mapping ---- */}
          <div style={{ flex: '2 1 560px', minWidth: 0 }}>
            <div className="form-grid-2" style={{ marginBottom: 8 }}>
              <div className="form-group" data-field="name">
                <label className="form-label">Profile name {!base && <span className="text-danger">*</span>}</label>
                <input
                  className={`form-input${fieldErrors.name ? ' form-field-error' : ''}`}
                  value={name}
                  disabled={!!base}
                  placeholder="e.g. Billing package — sales register"
                  onChange={(e) => { setName(e.target.value); setFieldErrors((f) => ({ ...f, name: '' })); }}
                />
                {fieldErrors.name && <div className="form-error-message">{fieldErrors.name}</div>}
              </div>
              {isHsn ? (
                <div className="form-group" data-field="summaryBasis">
                  <label className="form-label">HSN summary basis <span className="text-danger">*</span></label>
                  <select className={`form-select${fieldErrors.summaryBasis ? ' form-field-error' : ''}`} value={summaryBasis} onChange={(e) => setSummaryBasis(e.target.value)}>
                    <option value="">— choose —</option>
                    <option value="GROSS">Gross — before credit notes</option>
                    <option value="NET">Net — after credit notes</option>
                  </select>
                  {fieldErrors.summaryBasis && <div className="form-error-message">{fieldErrors.summaryBasis}</div>}
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Note for the reviewer</label>
                  <input className="form-input" value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-color)', marginBottom: 8 }}>
              {([['form', 'Columns'], ['json', 'Advanced (JSON)']] as [Mode, string][]).map(([m, label]) => (
                <button key={m} className="btn" onClick={() => switchMode(m)}
                  style={{ fontSize: '0.8125rem', padding: '4px 14px', borderRadius: 0, background: 'transparent', borderBottom: mode === m ? '2px solid #2563eb' : '2px solid transparent', color: mode === m ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: mode === m ? 600 : 400 }}>
                  {label}
                </button>
              ))}
            </div>

            {mode === 'json' && (
              <textarea
                className="form-textarea"
                value={json}
                onChange={(e) => setJson(e.target.value)}
                spellCheck={false}
                style={{ width: '100%', minHeight: 420, fontFamily: 'Consolas, monospace', fontSize: '0.75rem' }}
              />
            )}

            {mode === 'form' && (
              <>
                {form.sheets.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    {form.sheets.map((s) => (
                      <button key={s.sheet} className={`btn ${s.sheet === activeSheet ? 'btn-primary' : 'btn-secondary'}`}
                        style={{ fontSize: '0.75rem', padding: '3px 10px', opacity: s.include ? 1 : 0.6 }} onClick={() => setActiveSheet(s.sheet)}>
                        {s.sheet}{s.include ? '' : ' (not mapped)'}
                      </button>
                    ))}
                  </div>
                )}

                {current && (
                  <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8, fontSize: '0.8125rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
                        <input type="checkbox" checked={current.include} onChange={(e) => updateSheet((s) => ({ ...s, include: e.target.checked }))} />
                        Map this sheet
                      </label>
                      <div style={{ flex: '1 1 160px' }}>
                        <label className="form-label" title="Next month's sheet may be named differently — use * for the part that changes">Sheet name to match</label>
                        <input className="form-input" value={current.match} onChange={(e) => updateSheet((s) => ({ ...s, match: e.target.value }))} />
                      </div>
                      <div style={{ flex: '0 1 150px' }}>
                        <label className="form-label">Each row is</label>
                        <select className="form-select" value={current.recordKind} onChange={(e) => updateSheet((s) => ({ ...s, recordKind: e.target.value as SheetForm['recordKind'] }))}>
                          <option value="LINE">An invoice line</option>
                          <option value="DOCUMENT">A whole invoice</option>
                          <option value="SUMMARY">A summary line</option>
                        </select>
                      </div>
                      <div style={{ flex: '0 1 150px' }}>
                        <label className="form-label">Dates typed as</label>
                        <select className="form-select" value={current.dateFormat} onChange={(e) => updateSheet((s) => ({ ...s, dateFormat: e.target.value }))}>
                          <option value="">Date cells only</option>
                          {DATE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6 }} title="Dates stored as plain numbers such as 46248">
                        <input type="checkbox" checked={current.allowSerial} onChange={(e) => updateSheet((s) => ({ ...s, allowSerial: e.target.checked }))} />
                        Dates as numbers
                      </label>
                    </div>

                    {current.include && (
                      <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                          <thead>
                            <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                              <th style={{ padding: '3px 6px', width: '30%' }}>Field</th>
                              <th style={{ padding: '3px 6px', width: '30%' }}>Column in the file</th>
                              <th style={{ padding: '3px 6px', width: 70 }}>Required</th>
                              <th style={{ padding: '3px 6px' }}>First values</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleFields.map((f) => {
                              const idx = current.fields[f];
                              const inGroup = current.groups.length > 0 && (GROUP_FIELDS as readonly string[]).includes(f);
                              return (
                                <tr key={f}>
                                  <td style={cell}>{FIELD_NAMES[f] ?? f}</td>
                                  <td style={cell}>
                                    {inGroup || (f === 'rate' && current.groups.length > 0) ? (
                                      <span style={{ color: 'var(--text-secondary)' }}>from the rate-wise columns below</span>
                                    ) : (
                                      <select style={select} className="form-select" value={idx ?? ''} onChange={(e) => setField(f, e.target.value === '' ? null : Number(e.target.value))}>
                                        {columnOptions}
                                      </select>
                                    )}
                                  </td>
                                  <td style={{ ...cell, textAlign: 'center' }}>
                                    <input type="checkbox" checked={current.required.includes(f)} onChange={() => toggleRequired(f)} title="A row without this value is an error" />
                                  </td>
                                  <td style={{ ...cell, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{sampleOf(idx)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <button type="button" onClick={() => setShowAll((v) => !v)}
                          style={{ fontSize: '0.75rem', padding: '4px 0', border: 'none', background: 'transparent', color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                          {showAll ? 'Show the usual fields only' : 'Show all fields'}
                        </button>

                        {/* ---- rate-wise columns ---- */}
                        <div style={{ marginTop: 10, borderTop: '1px solid var(--border-color)', paddingTop: 8 }}>
                          <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>Rate-wise columns</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                            Only for a register with one row per invoice and a group of columns per rate (Taxable 5%, CGST 2.5% …). Each group becomes one record at that rate.
                          </div>
                          {current.groups.map((g, gi) => (
                            <div key={gi} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                              <input className="form-input" style={{ width: 70, fontSize: '0.8125rem', padding: '3px 6px' }} placeholder="Rate %" value={g.rate}
                                onChange={(e) => updateSheet((s) => ({ ...s, groups: s.groups.map((x, i) => (i === gi ? { ...x, rate: e.target.value.replace(/[^0-9.]/g, '') } : x)) }))} />
                              {GROUP_FIELDS.map((gf) => (
                                <select key={gf} className="form-select" style={{ ...select, width: 130 }} title={FIELD_NAMES[gf] ?? gf} value={g.columns[gf] ?? ''}
                                  onChange={(e) => updateSheet((s) => ({ ...s, groups: s.groups.map((x, i) => (i === gi ? { ...x, columns: { ...x.columns, [gf]: e.target.value === '' ? null : Number(e.target.value) } } : x)) }))}>
                                  <option value="">{FIELD_NAMES[gf] ?? gf}: —</option>
                                  {headers.map((_, i) => <option key={i} value={i}>{columnLabel(headers, i)}</option>)}
                                </select>
                              ))}
                              <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 8px' }} title="Remove this rate"
                                onClick={() => updateSheet((s) => ({ ...s, groups: s.groups.filter((_, i) => i !== gi) }))}>
                                <IconSystem.x size={12} />
                              </button>
                            </div>
                          ))}
                          <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                            onClick={() => updateSheet((s) => ({ ...s, groups: [...s.groups, { rate: '', columns: {} }] }))}>
                            Add a rate
                          </button>
                        </div>

                        {/* ---- unit map ---- */}
                        <div style={{ marginTop: 10, borderTop: '1px solid var(--border-color)', paddingTop: 8 }}>
                          <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>Units</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                            Map the client's own unit names to GSTN unit codes (e.g. Pcs → NOS, Kilogram → KGS).
                          </div>
                          {current.unitMap.map((u, ui) => (
                            <div key={ui} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                              <input className="form-input" style={{ width: 160, fontSize: '0.8125rem', padding: '3px 6px' }} placeholder="In the file" value={u.from}
                                onChange={(e) => updateSheet((s) => ({ ...s, unitMap: s.unitMap.map((x, i) => (i === ui ? { ...x, from: e.target.value } : x)) }))} />
                              <span style={{ color: 'var(--text-secondary)' }}>becomes</span>
                              <input className="form-input" style={{ width: 100, fontSize: '0.8125rem', padding: '3px 6px' }} placeholder="UQC" value={u.to}
                                onChange={(e) => updateSheet((s) => ({ ...s, unitMap: s.unitMap.map((x, i) => (i === ui ? { ...x, to: e.target.value.toUpperCase() } : x)) }))} />
                              <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 8px' }} title="Remove"
                                onClick={() => updateSheet((s) => ({ ...s, unitMap: s.unitMap.filter((_, i) => i !== ui) }))}>
                                <IconSystem.x size={12} />
                              </button>
                            </div>
                          ))}
                          <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                            onClick={() => updateSheet((s) => ({ ...s, unitMap: [...s.unitMap, { from: '', to: '' }] }))}>
                            Add a unit
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* ---- right: what the mapping makes of the file ---- */}
          <div style={{ flex: '1 1 300px', minWidth: 0, border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.625rem', position: 'sticky', top: 0 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 6 }}>
              Result on this file {previewing && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>· updating…</span>}
            </div>
            {fieldErrors.definition && !previewError && <div className="form-error-message" style={{ marginBottom: 6 }}>{fieldErrors.definition}</div>}
            {previewError && <div style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: 6 }}>{previewError}</div>}
            {preview && (
              <>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{preview.recordCount.toLocaleString()}</strong> records from {preview.inputRows.toLocaleString()} rows ·{' '}
                  <strong style={{ color: preview.recordsWithErrors ? '#dc2626' : 'var(--text-primary)' }}>{preview.recordsWithErrors.toLocaleString()}</strong> with errors ·{' '}
                  <strong style={{ color: preview.recordsWithWarnings ? '#d97706' : 'var(--text-primary)' }}>{preview.recordsWithWarnings.toLocaleString()}</strong> warnings
                </div>
                {preview.byRate.length > 0 && (
                  <table style={{ fontSize: '0.75rem', borderCollapse: 'collapse', width: '100%', marginBottom: 6 }}>
                    <thead>
                      <tr style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>Rate</th>
                        <th style={{ padding: '2px 4px' }}>Taxable</th>
                        <th style={{ padding: '2px 4px' }}>Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.byRate.map((t) => (
                        <tr key={String(t.rate)} style={{ textAlign: 'right', borderTop: '1px solid var(--border-color)' }}>
                          <td style={{ textAlign: 'left', padding: '2px 4px' }}>{t.rate === null || t.rate === undefined ? 'no rate' : `${t.rate}%`}</td>
                          <td style={{ padding: '2px 4px' }}>{money(t.taxableValue)}</td>
                          <td style={{ padding: '2px 4px' }}>{money(t.igst + t.cgst + t.sgst + t.cess)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {preview.messages.map((m, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', color: m.severity === 'ERROR' ? '#dc2626' : '#d97706' }}>{m.message}</div>
                ))}
                {preview.items.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>First rows with errors</div>
                    {preview.items.map((r) => (
                      <div key={`${r.sheet}-${r.rowIndex}`} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {r.sheet} row {r.rowIndex}: <span style={{ color: '#dc2626' }}>{r.errors[0]}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 8 }}>
              Tie these totals to the client's own summary before saving.
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto', justifyContent: 'space-between' }}>
          <label style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={useOnSubmission} onChange={(e) => setUseOnSubmission(e.target.checked)} />
            Use it on this submission
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || previewing}>Save for review</button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save profile for review?"
        message={`${base ? `This saves the next version of '${base.name}'` : `This saves '${name.trim()}'`} as a proposed profile for this client. It can be used on the client's files straight away; a reviewer accepts it before any return is finalised from it.`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void confirmSave(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard the mapping?"
        message="You have unsaved changes to this mapping. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default GstProfileMapperModal;
